import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getEvent, deleteEvent } from '../api/events';
import { useAuth } from '../contexts/AuthContext';
import { createWish, deleteWish, getWishes } from '../api/wishes';
import { getMoments } from '../api/moments';
import ConfirmModal from '../components/ConfirmModal';

function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const [event, setEvent] = useState(null);
  const [existingWishId, setExistingWishId] = useState(null);
  const [existingMoment, setExistingMoment] = useState(null);
  const [wishError, setWishError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  useEffect(() => {
    if (isAuthenticated) {
      const token = localStorage.getItem('auth_token');
      getWishes(token, id).then(data => {
        const wish = data.wishes?.[0];
        if (wish) setExistingWishId(wish.id);
      });
      getMoments(token, id).then(data => {
        const m = data.moments?.[0];
        if (m) setExistingMoment(m);
      });
    }
  }, [id, isAuthenticated]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const data = await getEvent(id);
      setEvent(data.event);
    } catch (err) {
      setError('Failed to load event');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteEvent(id);
      navigate('/events');
    } catch (err) {
      setError('Failed to delete event');
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleToggleWish = async () => {
    const token = localStorage.getItem('auth_token');
    try {
      if (existingWishId) {
        await deleteWish(existingWishId, token);
        setExistingWishId(null);
        setWishError(null);
      } else {
        const data = await createWish(id, token);
        setExistingWishId(data.wish.id);
        setWishError(null);
      }
    } catch (err) {
      setWishError('Failed to update wish. Please try again.');
    }
  };
  
  if (loading) return <div className="loading">Loading event...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!event) return <div className="error">Event not found</div>;

  return (
    <div className="event-detail">
      <button onClick={() => navigate(-1)} className="back-link">← Back</button>

      {event.url_image && (
        <img
          src={event.url_image}
          alt={event.title}
          className="event-detail-image"
        />
      )}

      <h1>{event.title}</h1>
      <p className="event-category">{event.category}</p>
      <p className="event-date">{formatDate(event.date)}</p>
      <div className="event-description">
        <p>{event.description}</p>
      </div>

      <div className="event-actions">
        {isAdmin && (
          <>
            <Link to={`/events/${id}/edit`} className="btn btn-secondary">Edit</Link>
            <button onClick={() => setShowDeleteModal(true)} className="btn btn-danger">Delete</button>
          </>
        )}
        {showDeleteModal && (
          <ConfirmModal
            title={`Delete "${event.title}"?`}
            message="This will permanently delete the event along with all associated wishes and moments. This action cannot be undone."
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteModal(false)}
          />
        )}
        {isAuthenticated && (
          <>
            <button
              onClick={handleToggleWish}
              className={`wish-heart-btn ${existingWishId ? 'wish-heart-btn--filled' : ''}`}
              aria-label={existingWishId ? 'Remove from wishes' : 'Add to wishes'}
            >
              {existingWishId ? '♥' : '♡'}
            </button>
            {wishError && (
              <div className="wish-error-popup">
                <p>⚠️ {wishError}</p>
                <button onClick={() => setWishError(null)}>✕</button>
              </div>
            )}

            {existingMoment ? (
              <Link to={`/moments/${existingMoment.id}`} className="btn btn-secondary">
                Voir mon moment →
              </Link>
            ) : (
              <Link to={`/events/${id}/moments/new`} className="btn btn-primary">
                + Ajouter un moment
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default EventDetail;
